import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import CarCardList from './CarCardList';
import { Card, Typography, Button, Tooltip } from "@material-tailwind/react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArchive, faClock, faEllipsisV, faEye, faPenAlt, faShieldAlt, faTrash, faUser } from '@fortawesome/free-solid-svg-icons'
import TimelineModal from '../car/timeline/AddTimelineNote';

const CarListing = ({ filteredCars, titles, handleDeleteCar, handleEditCar, handleArchiveCar, getInsuranceStatus, getBalanceStatus }) => {

    const [timelineModal, setTimelineModal] = useState({ open: false, plateNo: null });
    const [openMenu, setOpenMenu] = useState(null);
    const buttonRefs = useRef({});
    const menuPortalRef = useRef(null);

    const MENU_WIDTH = 176;
    const MENU_GAP = 8;
    const MENU_MARGIN = 12;
    const MENU_ESTIMATED_HEIGHT = 248;

    const openTimelineModal = (plateNo) => {
        setTimelineModal({ open: true, plateNo });
    };

    const closeTimelineModal = () => {
        setTimelineModal({ open: false, plateNo: null });
    };

    const closeActionMenu = () => {
        setOpenMenu(null);
    };

    const positionActionMenu = (plateNo) => {
        const button = buttonRefs.current[plateNo];
        if (!button) return null;

        const rect = button.getBoundingClientRect();
        const spaceAbove = rect.top - MENU_MARGIN;
        const spaceBelow = window.innerHeight - rect.bottom - MENU_MARGIN;
        const openUp = spaceBelow < MENU_ESTIMATED_HEIGHT && spaceAbove > spaceBelow;
        const top = openUp ? rect.top - MENU_GAP : rect.bottom + MENU_GAP;
        const left = Math.max(
            MENU_MARGIN,
            Math.min(rect.right - MENU_WIDTH, window.innerWidth - MENU_WIDTH - MENU_MARGIN)
        );

        return { top, left, openUp };
    };

    const openActionMenu = (plateNo) => {
        setOpenMenu((current) => {
            if (current?.plateNo === plateNo) {
                return null;
            }

            const position = positionActionMenu(plateNo);
            if (!position) return current;

            return { plateNo, ...position };
        });
    };

    const navigate = useNavigate();

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!openMenu) return;

            const target = event.target;
            const activeMenu = menuPortalRef.current;
            const activeButton = buttonRefs.current[openMenu.plateNo];
            const clickedInsideMenu = activeMenu?.contains(target);
            const clickedInsideButton = activeButton?.contains(target);

            if (!clickedInsideMenu && !clickedInsideButton) {
                closeActionMenu();
            }
        };

        const handleEscape = (event) => {
            if (event.key === 'Escape') {
                closeActionMenu();
            }
        };

        const handleReposition = () => {
            if (!openMenu?.plateNo) return;
            const position = positionActionMenu(openMenu.plateNo);
            if (!position) return;
            setOpenMenu((current) => current?.plateNo === openMenu.plateNo ? { ...current, ...position } : current);
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleEscape);
        window.addEventListener('resize', handleReposition);
        window.addEventListener('scroll', handleReposition, true);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEscape);
            window.removeEventListener('resize', handleReposition);
            window.removeEventListener('scroll', handleReposition, true);
        };
    }, [openMenu]);

    if (!filteredCars || filteredCars.length === 0) {
        return (
            <div className="text-center py-8">
                <p className="text-brand-secondary text-lg">No cars available.</p>
            </div>
        );
    }

    // Helper function to get last transaction date color based on recency
    const getLastTransactionColor = (lastTransDate) => {
        if (!lastTransDate) return 'text-gray-600 bg-gray-50 px-2 py-1 rounded-md'; // Gray for cars with no transaction data

        const today = new Date();
        const transDate = new Date(lastTransDate);
        const daysAgo = Math.floor((today - transDate) / (1000 * 60 * 60 * 24));

        if (daysAgo <= 1) {
            return 'text-green-700 font-semibold bg-green-50 px-2 py-1 rounded-md'; // 🟢 Green for recent transactions (within 1 day)
        } else if (daysAgo <= 2) {
            return 'text-blue-700 font-semibold bg-blue-50 px-2 py-1 rounded-md'; // 🔵 Blue for transactions within 2 days
        } else if (daysAgo <= 3) {
            return 'text-amber-700 font-semibold bg-amber-50 px-2 py-1 rounded-md'; // 🟠 Amber for transactions within 3 days
        } else {
            return 'text-red-700 font-semibold bg-red-50 px-2 py-1 rounded-md'; // 🔴 Red for transactions older than 4 days
        }
    };

    // Helper function to get status color based on status name and properties
    const getStatusColor = (statusName) => {
        if (!statusName) return { bg: 'bg-gray-100', text: 'text-gray-600', border: 'border-gray-200' };

        const status = statusName.toString().toLowerCase().trim();

        // Active/Operational statuses (green)
        if (status.includes('active') || status.includes('operational') ||
            status.includes('available') || status.includes('working') ||
            status.includes('assigned')) {
            return { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-200' };
        }

        // Warning statuses (orange/yellow)
        if (status.includes('maintenance') || status.includes('service') ||
            status.includes('repair') || status.includes('pending') ||
            status.includes('reserved')) {
            return { bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-200' };
        }

        // Inactive/Problematic statuses (red)
        if (status.includes('inactive') || status.includes('retired') ||
            status.includes('out of service') || status.includes('suspended') ||
            status.includes('disabled') || status.includes('broken')) {
            return { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-200' };
        }

        // Default active status (blue)
        return { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-200' };
    };

    const formatShortDate = (value) => {
        if (!value) return '';
        const date = new Date(value);
        return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    const renderCellValue = (car, value) => {
        switch (value) {
            case 'account_manager_name':
                return car.account_manager_name ? (
                    <Typography variant="small" className="font-normal text-xs text-brand-secondary truncate" title={car.account_manager_name}>
                        {car.account_manager_name}
                    </Typography>
                ) : (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-sm text-[11px] font-medium whitespace-nowrap bg-amber-50 text-amber-700 border border-amber-200">
                        Unassigned
                    </span>
                );

            case 'actual_bal':
                const balanceStatus = getBalanceStatus(car[value]);
                return (
                    <Typography variant="small" className={`font-normal text-xs whitespace-nowrap ${balanceStatus.color}`}>
                        {Number(car[value]).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </Typography>
                );

            case 'insurer_expiry':
                const insuranceStatus = getInsuranceStatus(car[value]);
                return (
                    <Typography variant="small" className={`font-normal text-xs ${insuranceStatus.color}`}>
                        {car[value] || 'N/A'}
                    </Typography>
                );

            case 'status':
                const statusColors = getStatusColor(car[value]);
                return (
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-sm text-[11px] font-medium whitespace-nowrap ${statusColors.bg} ${statusColors.text} border ${statusColors.border}`}>
                        {car[value]}
                    </span>
                );

            case 'next_government_inspection_due':
                return (
                    <Typography variant="small" className="font-normal text-xs text-brand-secondary whitespace-nowrap">
                        {formatShortDate(car[value]) || 'Never done'}
                    </Typography>
                );

            case 'last_job_card_date':
                return car[value] ? (
                    <div className="text-xs leading-tight">
                        <div className="text-brand-secondary whitespace-nowrap">{formatShortDate(car[value])}</div>
                        <div className="text-gray-500 truncate">{car.last_job_card_no}</div>
                    </div>
                ) : (
                    <Typography variant="small" className="font-normal text-xs text-gray-400">None</Typography>
                );

            case 'usage_type':
                const isPrivate = car[value] === 'PRIVATE';
                return (
                    <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-sm text-[11px] font-medium whitespace-nowrap ${
                            isPrivate
                                ? 'bg-purple-100 text-purple-800 border border-purple-200'
                                : 'bg-sky-100 text-sky-800 border border-sky-200'
                        }`}
                    >
                        {isPrivate ? 'Private' : 'Business'}
                    </span>
                );

            case 'is_discounted':
                const isDiscounted = Boolean(car.discount_amount) || Boolean(car[value]);
                return (
                    <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-sm text-[11px] font-medium whitespace-nowrap ${
                            isDiscounted
                                ? 'bg-amber-100 text-amber-800 border border-amber-200'
                                : 'bg-green-100 text-green-700 border border-green-200'
                        }`}
                    >
                        {isDiscounted ? 'Discounted' : 'Billable'}
                    </span>
                );

            default:
                return (
                    <Tooltip content={car[value] || 'N/A'} placement="top">
                        <Typography variant="small" color="blue-gray" className="font-normal text-brand-secondary text-xs truncate cursor-help">
                            {car[value] || 'N/A'}
                        </Typography>
                    </Tooltip>
                );
        }
    };

    const getColumnClasses = (columnValue) => {
        const classes = {
            plate_no: 'w-[92px] max-w-[92px]',
            insurer_expiry: 'w-[100px] max-w-[100px]',
            actual_bal: 'w-[92px] max-w-[92px] text-right',
            status: 'w-[96px] max-w-[96px]',
            usage_type: 'w-[84px] max-w-[84px]',
            owner_code: 'w-[96px] max-w-[96px]',
            next_government_inspection_due: 'w-[104px] max-w-[104px]',
            last_job_card_date: 'w-[112px] max-w-[112px]',
            is_discounted: 'w-[92px] max-w-[92px]',
            driver_name: 'w-[108px] max-w-[108px]',
            account_manager_name: 'w-[112px] max-w-[112px]',
            action: 'w-[60px] max-w-[60px] text-center',
            last_payment: 'w-[82px] max-w-[82px]',
        };

        return classes[columnValue] || 'w-[100px] max-w-[100px]';
    };

    const renderActionMenu = (car) => (
        <div
            className="relative flex justify-center"
        >
            <Button
                ref={(node) => {
                    if (node) {
                        buttonRefs.current[car.plate_no] = node;
                    } else {
                        delete buttonRefs.current[car.plate_no];
                    }
                }}
                size="sm"
                variant="text"
                className="flex h-8 w-8 items-center justify-center rounded-sm p-0 text-slate-600 hover:bg-slate-100 shadow-none"
                onClick={() => openActionMenu(car.plate_no)}
            >
                <FontAwesomeIcon icon={faEllipsisV} className="text-sm" />
            </Button>
        </div>
    );

    // Find the index of the status column
    const statusIndex = titles.findIndex(t => t.value === 'status');

    const actionMenu = openMenu && typeof document !== 'undefined' ? createPortal(
        <div
            ref={menuPortalRef}
            data-car-action-menu="true"
            className="fixed min-w-[176px] overflow-hidden rounded-sm border border-slate-200 bg-white py-1 text-left shadow-2xl"
            style={{
                top: `${openMenu.top}px`,
                left: `${openMenu.left}px`,
                zIndex: 99999,
                transform: openMenu.openUp ? 'translateY(-100%)' : 'none',
            }}
        >
            <button
                type="button"
                className="flex w-full items-center gap-2 px-3 py-2 text-xs text-slate-700 hover:bg-slate-100"
                onClick={() => {
                    closeActionMenu();
                    navigate(`/cars/${openMenu.plateNo}`);
                }}
            >
                <FontAwesomeIcon icon={faEye} className="w-3" />
                View
            </button>
            <button
                type="button"
                className="flex w-full items-center gap-2 px-3 py-2 text-xs text-slate-700 hover:bg-slate-100"
                onClick={() => {
                    closeActionMenu();
                    navigate(`/cars/drivers/${openMenu.plateNo}`);
                }}
            >
                <FontAwesomeIcon icon={faUser} className="w-3" />
                Assign Driver
            </button>
            <button
                type="button"
                className="flex w-full items-center gap-2 px-3 py-2 text-xs text-slate-700 hover:bg-slate-100"
                onClick={() => {
                    closeActionMenu();
                    navigate(`/cars/insurances/${openMenu.plateNo}`);
                }}
            >
                <FontAwesomeIcon icon={faShieldAlt} className="w-3" />
                Insurances
            </button>
            <button
                type="button"
                className="flex w-full items-center gap-2 px-3 py-2 text-xs text-slate-700 hover:bg-slate-100"
                onClick={() => {
                    closeActionMenu();
                    handleEditCar(openMenu.plateNo);
                }}
            >
                <FontAwesomeIcon icon={faPenAlt} className="w-3" />
                Edit
            </button>
            <button
                type="button"
                className="flex w-full items-center gap-2 px-3 py-2 text-xs text-slate-700 hover:bg-slate-100"
                onClick={() => {
                    closeActionMenu();
                    openTimelineModal(openMenu.plateNo);
                }}
            >
                <FontAwesomeIcon icon={faClock} className="w-3" />
                Timeline Note
            </button>
            <button
                type="button"
                className="flex w-full items-center gap-2 px-3 py-2 text-xs text-amber-700 hover:bg-amber-50"
                onClick={() => {
                    closeActionMenu();
                    handleArchiveCar && handleArchiveCar(openMenu.plateNo);
                }}
            >
                <FontAwesomeIcon icon={faArchive} className="w-3" />
                Archive
            </button>
            <button
                type="button"
                className="flex w-full items-center gap-2 px-3 py-2 text-xs text-red-600 hover:bg-red-50"
                onClick={() => {
                    closeActionMenu();
                    handleDeleteCar(openMenu.plateNo);
                }}
            >
                <FontAwesomeIcon icon={faTrash} className="w-3" />
                Delete
            </button>
        </div>,
        document.body
    ) : null;

    return (
        <Card className="h-full w-full overflow-auto overflow-x-auto border border-gray-100 shadow-none">
            {/* Mobile card list - visible only on small screens */}
            <CarCardList cars={filteredCars} onView={(p) => navigate(`/cars/${p}`)} onEdit={(p) => handleEditCar(p)} onDelete={(p) => handleDeleteCar(p)} />

            <div className="hidden sm:block min-w-0 max-w-full">
                <table className="w-full min-w-[860px] table-auto text-left">
                    <thead>
                        <tr className="border-b border-blue-gray-100 bg-[#EDE4DA]">
                            {titles.map((head, idx) => {
                                // Insert LT/Date column before status column
                                if (idx === statusIndex) {
                                    return [
                                        <th key="ltdate" className={`p-2 align-middle ${getColumnClasses('last_payment')}`}>
                                            <Typography variant="small" color="blue-gray" className="font-semibold leading-none text-brand-secondary text-xs">
                                                Last Payment
                                            </Typography>
                                        </th>,
                                        <th key={head.value} className={`p-2 align-middle ${getColumnClasses(head.value)}`}>
                                            <Typography
                                                variant="small"
                                                color="blue-gray"
                                                className="font-semibold leading-none text-brand-secondary text-xs"
                                            >
                                                {head.label}
                                            </Typography>
                                        </th>
                                    ];
                                }
                                return (
                                    <th key={head.value} className={`p-2 align-middle ${getColumnClasses(head.value)}`}>
                                        <Typography
                                            variant="small"
                                            color="blue-gray"
                                            className="font-semibold leading-none text-brand-secondary text-xs"
                                        >
                                            {head.label}
                                        </Typography>
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {filteredCars.map((car) => (
                            <tr key={car.plate_no} className="border-b border-blue-gray-50 even:bg-gray-50/50 hover:bg-blue-gray-50/50 transition-colors duration-200">
                                {titles.map(({ value }, idx) => {

                                    if (idx === statusIndex) {
                                        return [
                                            <td key="ltdate" className={`p-2 align-middle ${getColumnClasses('last_payment')}`}>
                                                {(() => {
                                                    const dateSource = car.last_war_date ?? car.last_trans_date;
                                                    const dateLabel = dateSource ? new Date(dateSource).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : 'N/A';
                                                    const fullDateLabel = dateLabel;
                                                    const warType = car.last_war_type ?? 'N/A';
                                                    const amountRaw = car.last_war_amount ?? null;
                                                    const amount = amountRaw !== null && amountRaw !== undefined && amountRaw !== '' ? Number(amountRaw).toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : 'N/A';
                                                    const ref = car.last_war_ref ?? car.last_trans_your_ref ?? 'N/A';
                                                    const inputBy = car.last_war_input_by ?? car.last_trans_by ?? 'N/A';
                                                    const tooltipContent = (
                                                        <div role="tooltip" className="text-white p-3 rounded-lg shadow-lg max-w-xs text-sm">
                                                            <div className="space-y-1">
                                                                <div className="flex justify-between">
                                                                    <span className="text-gray-300">Type:</span>
                                                                    <span className="font-medium text-white ml-4">{warType}</span>
                                                                </div>
                                                                <div className="flex justify-between">
                                                                    <span className="text-gray-300">Amount:</span>
                                                                    <span className="font-medium text-white ml-4">{amount}</span>
                                                                </div>
                                                                <div className="flex justify-between">
                                                                    <span className="text-gray-300">Date:</span>
                                                                    <span className="font-medium text-white ml-4">{fullDateLabel}</span>
                                                                </div>
                                                                <div className="flex justify-between">
                                                                    <span className="text-gray-300">Ref:</span>
                                                                    <span className="font-mono text-white ml-4 break-words">{ref}</span>
                                                                </div>
                                                                <div className="flex justify-between">
                                                                    <span className="text-gray-300">By:</span>
                                                                    <span className="font-medium text-white ml-4">{inputBy}</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                    return (
                                                        <Tooltip content={tooltipContent} placement="top">
                                                            <Typography variant="small" color="blue-gray" className={`font-normal text-xs truncate cursor-help ${getLastTransactionColor(dateSource)}`}>
                                                                {dateLabel}
                                                            </Typography>
                                                        </Tooltip>
                                                    );
                                                })()}
                                            </td>,
                                            <td key={value} className={`p-2 align-middle ${getColumnClasses(value)} ${value.class || ''}`}>
                                                {value === 'action' ? (
                                                    renderActionMenu(car)
                                                ) : (
                                                    renderCellValue(car, value)
                                                )}
                                            </td>
                                        ];
                                    }
                                    return (
                                        <td key={value} className={`p-2 align-middle ${getColumnClasses(value)} ${value.class || ''}`}>
                                            {value === 'action' ? (
                                                renderActionMenu(car)
                                            ) : (
                                                renderCellValue(car, value)
                                            )}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <TimelineModal
                open={timelineModal.open}
                plateNo={timelineModal.plateNo}
                onClose={closeTimelineModal}
                onSaved={() => {
                    // Close modal and refresh to show new timeline note
                    closeTimelineModal();
                    try { window.location.reload(); } catch (e) { /* ignore */ }
                }}
            />
            {actionMenu}
        </Card>
    );
};

export default CarListing;
